import type { LocalFileCandidate, LocalSelection } from './ports';

const safePath = (value: string): string =>
  value
    .replaceAll('\\', '/')
    .split('/')
    .filter((segment) => segment.length > 0 && segment !== '.' && segment !== '..')
    .join('/')
    .slice(0, 1024);

const rootFromPath = (path: string, fallback: string): string =>
  safePath(path)
    .split('/')
    .find((segment) => segment.length > 0) ?? fallback;

export const candidateFromFile = (file: File, forcedPath?: string): LocalFileCandidate => {
  const browserPath = 'webkitRelativePath' in file ? file.webkitRelativePath : '';
  const selectedPath = [forcedPath, browserPath, file.name].find(
    (value): value is string => value !== undefined && value.length > 0,
  );
  const normalizedPath = safePath(selectedPath ?? file.name);
  const relativePath = normalizedPath.length > 0 ? normalizedPath : file.name;
  return {
    name: file.name,
    relativePath,
    rootName: rootFromPath(relativePath, file.name),
    mediaType: file.type,
    size: file.size,
    lastModified: file.lastModified,
    readBytes: async () => new Uint8Array(await file.arrayBuffer()),
  };
};

export const selectionFromFiles = (
  files: FileList | readonly File[],
  origin: LocalSelection['origin'],
  folder: boolean,
): LocalSelection => {
  const discovered = [...files].map((file) => candidateFromFile(file));
  const vault =
    folder &&
    discovered.some((candidate) => /(^|\/)\.obsidian(\/|$)/u.test(candidate.relativePath));
  const candidates = discovered.filter(
    (candidate) =>
      !/(^|\/)\.obsidian(\/|$)/u.test(candidate.relativePath) &&
      !/(^|\/)(?:\.DS_Store|Thumbs\.db)$/iu.test(candidate.relativePath),
  );
  return {
    candidates,
    containerKind: folder
      ? vault
        ? 'vault'
        : 'folder'
      : candidates.length === 1
        ? 'file'
        : 'selection',
    origin,
  };
};

const readFileEntry = (entry: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => entry.file(resolve, reject));

const readDirectoryEntries = async (
  entry: FileSystemDirectoryEntry,
): Promise<readonly FileSystemEntry[]> => {
  const reader = entry.createReader();
  const entries: FileSystemEntry[] = [];
  const readNext = async (): Promise<readonly FileSystemEntry[]> => {
    const batch = await new Promise<readonly FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return entries;
    entries.push(...batch);
    return readNext();
  };
  return readNext();
};

const walkEntry = async (
  entry: FileSystemEntry,
  prefix: string,
): Promise<readonly LocalFileCandidate[]> => {
  const path = safePath(prefix ? `${prefix}/${entry.name}` : entry.name);
  if (entry.isFile) {
    return [candidateFromFile(await readFileEntry(entry as FileSystemFileEntry), path)];
  }
  if (entry.isDirectory) {
    const children = await readDirectoryEntries(entry as FileSystemDirectoryEntry);
    const nested = await Promise.all(children.map((child) => walkEntry(child, path)));
    return nested.flat();
  }
  return [];
};

export const selectionFromDrop = async (dataTransfer: DataTransfer): Promise<LocalSelection> => {
  const entries = [...dataTransfer.items]
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null);

  let candidates: readonly LocalFileCandidate[];
  let folder = false;
  if (entries.length > 0) {
    folder = entries.some((entry) => entry.isDirectory);
    candidates = (await Promise.all(entries.map((entry) => walkEntry(entry, '')))).flat();
  } else {
    candidates = [...dataTransfer.files].map((file) => candidateFromFile(file));
  }
  const deduplicated = [
    ...new Map(candidates.map((candidate) => [candidate.relativePath, candidate])).values(),
  ];
  const vault =
    folder &&
    deduplicated.some((candidate) => /(^|\/)\.obsidian(\/|$)/u.test(candidate.relativePath));
  const accepted = deduplicated.filter(
    (candidate) =>
      !/(^|\/)\.obsidian(\/|$)/u.test(candidate.relativePath) &&
      !/(^|\/)(?:\.DS_Store|Thumbs\.db)$/iu.test(candidate.relativePath),
  );
  return {
    candidates: accepted,
    containerKind: folder
      ? vault
        ? 'vault'
        : 'folder'
      : accepted.length === 1
        ? 'file'
        : 'selection',
    origin: 'drop',
  };
};
